import {
  PUBLISHER_BULK_MAX_IDS,
  type PublisherAction,
  type PublisherWithStats,
  publisherActionToStatus,
  type StaffRole,
} from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  Switch,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useMemo, useState } from 'react';
import { SegmentedControl, type SegmentOption } from '../components/filters/segmented-control.js';
import { BulkActionBar } from '../components/publishers/bulk-action-bar.js';
import { ProvisionPublisherModal } from '../components/publishers/provision-publisher-modal.js';
import type { StatusAction } from '../components/publishers/publisher-actions.js';
import { ACTION_VERB } from '../components/publishers/publisher-status-map.js';
import { PublishersTable } from '../components/publishers/publishers-table.js';
import { SearchInput } from '../components/search-input.js';
import { usePublishersListContent } from '../content/sections/publishers-list.js';
import { useLimitedBulkSelection } from '../hooks/use-limited-bulk-selection.js';
import {
  useBulkPublisherAction,
  usePublishersList,
  useSetPublisherStatus,
  useVerifyPublisher,
} from '../hooks/use-publishers.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];
const STATUS_FILTERS = ['all', 'pending', 'verified', 'suspended', 'banned'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

/* Filtros client-side sobre a lista já carregada (ADR-0702/0704): estado das chaves + backlog.
   Só derivam de campos presentes no `PublisherWithStats` (keys/stats) — sem chamada extra.
   `pendingKeys` espelha a lógica de `needsVerify` (chave sem root-sign e não revogada). */
const KEY_FILTERS = ['all', 'pendingKeys', 'revokedKeys'] as const;
type KeyFilter = (typeof KEY_FILTERS)[number];

const hasPendingKeys = (p: PublisherWithStats): boolean =>
  p.keys.some((k) => !k.rootSigned && !k.revoked);
const hasRevokedKeys = (p: PublisherWithStats): boolean => p.keys.some((k) => k.revoked);
const hasBacklog = (p: PublisherWithStats): boolean => (p.stats?.pendingSubmissions ?? 0) > 0;

type Pending =
  | {
      readonly kind: 'single';
      readonly publisher: PublisherWithStats;
      readonly action: StatusAction;
    }
  | { readonly kind: 'bulk'; readonly ids: readonly string[]; readonly action: PublisherAction };

export function PublishersListPage() {
  const copy = usePublishersListContent();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const isSuperAdmin = session?.role === 'super_admin';

  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [keyFilter, setKeyFilter] = useState<KeyFilter>('all');
  const [backlogOnly, setBacklogOnly] = useState(false);
  const { data, isLoading, isError } = usePublishersList(filter === 'all' ? undefined : filter);
  const verify = useVerifyPublisher();
  const setStatus = useSetPublisherStatus();
  const bulk = useBulkPublisherAction();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [provisionOpen, setProvisionOpen] = useState(false);

  const publishers = useMemo(() => data ?? [], [data]);
  /* Busca client-side sobre a lista já carregada (ADR-0702) — o status já é filtrado no servidor. */
  const query = search.trim().toLowerCase();
  const searchFiltered = useMemo(
    () =>
      query.length === 0
        ? publishers
        : publishers.filter(
            (p) =>
              p.displayName.toLowerCase().includes(query) || p.slug.toLowerCase().includes(query),
          ),
    [publishers, query],
  );
  /* Estado das chaves + backlog aplicados sobre o conjunto já buscado (ADR-0704). */
  const filtered = useMemo(
    () =>
      searchFiltered.filter((p) => {
        if (keyFilter === 'pendingKeys' && !hasPendingKeys(p)) return false;
        if (keyFilter === 'revokedKeys' && !hasRevokedKeys(p)) return false;
        if (backlogOnly && !hasBacklog(p)) return false;
        return true;
      }),
    [searchFiltered, keyFilter, backlogOnly],
  );
  const visiblePublisherIds = useMemo(() => filtered.map((publisher) => publisher.id), [filtered]);
  const selection = useLimitedBulkSelection(visiblePublisherIds, PUBLISHER_BULK_MAX_IDS);
  /* Contadores por estado de chave sobre o conjunto buscado — informa antes de aplicar o filtro. */
  const keyOptions = useMemo<readonly SegmentOption<KeyFilter>[]>(
    () => [
      { value: 'all', label: copy.keyFilterLabels.all, count: searchFiltered.length },
      {
        value: 'pendingKeys',
        label: copy.keyFilterLabels.pendingKeys,
        count: searchFiltered.filter(hasPendingKeys).length,
      },
      {
        value: 'revokedKeys',
        label: copy.keyFilterLabels.revokedKeys,
        count: searchFiltered.filter(hasRevokedKeys).length,
      },
    ],
    [searchFiltered, copy.keyFilterLabels],
  );
  const statusOptions = useMemo<readonly SegmentOption<StatusFilter>[]>(
    () => STATUS_FILTERS.map((f) => ({ value: f, label: copy.statusFilterLabels[f] })),
    [copy.statusFilterLabels],
  );
  const hasFilters = filter !== 'all' || query.length > 0 || keyFilter !== 'all' || backlogOnly;
  const clearFilters = (): void => {
    setFilter('all');
    setSearch('');
    setKeyFilter('all');
    setBacklogOnly(false);
  };
  const isBusy = setStatus.isPending || bulk.isPending;

  const onVerify = (id: string): void => {
    setBusyId(id);
    verify.mutate(id, {
      onSuccess: (result) => toast.success(copy.verifySuccess(result.signedKeyIds.length)),
      onError: () => toast.error(copy.verifyError),
      onSettled: () => setBusyId(null),
    });
  };

  const closeDialog = (): void => {
    if (isBusy) return;
    setPending(null);
    setReason('');
  };

  const confirm = (): void => {
    if (pending === null || reason.trim().length === 0) return;
    const trimmed = reason.trim();
    if (pending.kind === 'single') {
      setStatus.mutate(
        {
          id: pending.publisher.id,
          status: publisherActionToStatus[pending.action],
          reason: trimmed,
        },
        {
          onSuccess: () => {
            toast.success(copy.actionAppliedSuccess(ACTION_VERB[pending.action]));
            setPending(null);
            setReason('');
          },
          onError: () => toast.error(copy.actionAppliedError(ACTION_VERB[pending.action])),
        },
      );
      return;
    }
    bulk.mutate(
      { ids: [...pending.ids], action: pending.action, reason: trimmed },
      {
        onSuccess: (res) => {
          const failed = res.results.filter((r) => !r.ok).length;
          if (failed === 0) toast.success(copy.bulkActionSuccess(res.results.length));
          else toast.warning(copy.bulkActionPartial(res.results.length - failed, failed));
          setPending(null);
          setReason('');
          selection.clear();
        },
        onError: () => toast.error(copy.bulkActionError),
      },
    );
  };

  const requestBulk = (action: PublisherAction): void =>
    setPending({ kind: 'bulk', ids: [...selection.selectedIds], action });

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        {isSuperAdmin && (
          <PageActions>
            <Button onClick={() => setProvisionOpen(true)}>{copy.provisionButton}</Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <InlineAlert variant="info">{copy.governanceAlert}</InlineAlert>

        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            ariaLabel={copy.searchLabel}
            placeholder={copy.searchPlaceholder}
          />
          <SegmentedControl
            value={filter}
            onChange={setFilter}
            options={statusOptions}
            ariaLabel={copy.statusFilterAriaLabel}
          />
          <SegmentedControl
            value={keyFilter}
            onChange={setKeyFilter}
            options={keyOptions}
            ariaLabel={copy.keyFilterAriaLabel}
          />
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Switch
              id="publishers-backlog"
              checked={backlogOnly}
              onCheckedChange={setBacklogOnly}
            />
            <Label htmlFor="publishers-backlog">{copy.backlogLabel}</Label>
          </div>
          {/* follow-up: filtro por data (sem primitive date-range) e por domínio verificado
              (não está no list DTO PublisherWithStats) — exigem contrato/primitive novos. */}
        </div>

        {canManage && (
          <BulkActionBar
            selectedCount={selection.selectedIds.size}
            isSuperAdmin={isSuperAdmin}
            busy={isBusy}
            onApply={requestBulk}
            onClear={selection.clear}
          />
        )}

        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canManage ? 6 : 5} />
        ) : (
          <PublishersTable
            publishers={filtered}
            canManage={canManage}
            isSuperAdmin={isSuperAdmin}
            selectedIds={selection.selectedIds}
            busyId={busyId}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
            onVerify={onVerify}
            onRequestAction={(publisher, action) =>
              setPending({ kind: 'single', publisher, action })
            }
          />
        )}

        <Dialog open={pending !== null} onOpenChange={(o) => !o && closeDialog()}>
          <DialogContent locked={isBusy}>
            <DialogHeader>
              <DialogTitle>{copy.confirmDialogTitle}</DialogTitle>
              <DialogDescription>
                {pending?.kind === 'bulk'
                  ? copy.bulkConfirmMessage(ACTION_VERB[pending.action], pending.ids.length)
                  : pending
                    ? copy.singleConfirmMessage(
                        ACTION_VERB[pending.action],
                        pending.publisher.displayName,
                      )
                    : ''}{' '}
                {copy.confirmReasonHint}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor="publisher-action-reason">{copy.reasonLabel}</Label>
              <Input
                id="publisher-action-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={copy.reasonPlaceholder}
                disabled={isBusy}
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={closeDialog} disabled={isBusy}>
                {copy.cancelButton}
              </Button>
              <Button
                variant={pending?.action === 'ban' ? 'destructive' : 'default'}
                loading={isBusy}
                disabled={reason.trim().length === 0}
                onClick={confirm}
              >
                {copy.confirmButton}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ProvisionPublisherModal open={provisionOpen} onClose={() => setProvisionOpen(false)} />
      </PageContent>
    </Page>
  );
}
